import * as React from 'react';
import * as _ from 'lodash';
import { makeObservable, observable } from 'mobx';

import {
  AbstractBatcherBank,
  AbstractBatcherBankGenerics,
  Banks,
  FetchCollectionParams
} from '@journeyapps-platform/lib-journey-core';
import { MFAFactor, MFAFactorState, UserMFAFactorResource } from '@journeyapps-platform/types-accounts-hub';

import { System } from '../System';
import { Layer } from '../layers/LayerManager';
import { SensitiveUserAuthWidget } from '../widgets/authentication/SensitiveUserAuthWidget';
import { AbstractMFAFactorModel } from '../models/AbstractMFAFactorModel';
import { SMSMFAModel } from '../models/SMSMFAFactorModel';
import { AdminClientProvisioner } from '../clients';
import { AuthenticatorMFAModel } from '../models/AuthenticatorMFAModel';

export type MFAAuthenticationHandle = {
  resolver: (token: string) => void;
  rejecter: (error?: Error) => void;
};

export type MFAStoreGenerics = AbstractBatcherBankGenerics & {
  MODEL: AbstractMFAFactorModel;
  DATA: UserMFAFactorResource;
  OPTIONS: MFAStoreOptions;
};

export type MFAStoreOptions = {
  banks: () => Banks;
  clients: AdminClientProvisioner;
};

export type MFAFactorModelMap = {
  [MFAFactor.SMS]: SMSMFAModel;
  [MFAFactor.TOTP]: AuthenticatorMFAModel;
};

export class MFAStore extends AbstractBatcherBank<MFAStoreGenerics> {
  /**
   * For showing authentication prompt when needed
   */
  authenticationHandlers: MFAAuthenticationHandle[];
  private MFAAuthenticationLayer: Layer | null;

  @observable
  preferredMFAFactor: AbstractMFAFactorModel | null;

  @observable
  backupCodesViewed: boolean;

  constructor(
    protected system: System,
    options: MFAStoreOptions
  ) {
    super(options);

    this.preferredMFAFactor = null;

    this.authenticationHandlers = [];
    this.MFAAuthenticationLayer = null;
    this.backupCodesViewed = false;

    makeObservable(this);
  }

  async init() {
    try {
      await this.loadUserMFAConfig();
      return true;
    } catch (Exception) {
      return false;
    }
  }

  async loadUserMFAConfig() {
    const { factor } = await this.system.accountV5Client.getUserPreferredMFAFactor();
    this.preferredMFAFactor = factor ? this.generate(factor) : null;

    await this.loadMFAFactors();
  }

  generate(data: UserMFAFactorResource): AbstractMFAFactorModel {
    switch (data.factor_type) {
      case MFAFactor.SMS:
        return new SMSMFAModel({
          clients: this.options.clients,
          model: data,
          banks: this.options.banks()
        });
      case MFAFactor.TOTP:
        return new AuthenticatorMFAModel({
          clients: this.options.clients,
          model: data,
          banks: this.options.banks()
        });
    }
  }

  getActiveFactor<Type extends MFAFactor = MFAFactor>(type: Type): MFAFactorModelMap[Type] | null {
    return (
      (_.values(this.models).find(
        (model) => model.options.model.state == MFAFactorState.ACTIVE && model.factorType == type
      ) as MFAFactorModelMap[Type]) ?? null
    );
  }

  /**
   * Cleanup any factors which were not verified successfully
   * and are stale
   */
  async cleanupPendingFactors() {
    return Promise.all(
      _.chain(this.models)
        .values()
        .filter((factor) => factor.options.model.state == MFAFactorState.PENDING)
        .map(async (factor) => {
          await factor.delete();
          delete this.models[factor.id];
        })
        .value()
    );
  }

  protected async fetchCollection(event: FetchCollectionParams<UserMFAFactorResource>): Promise<void> {
    const res = await this.options.clients.provisionAccountsV5Client().listMFAFactors();

    this.backupCodesViewed = res.backup_codes_viewed;

    this.models = {};
    for (let factor of res.factors) {
      event.apply({ [factor.id]: factor });
    }
  }

  protected async loadMFAFactors() {
    await this.fetchCollection({
      items: [],
      apply: (results) => {
        this.merge(results);
      },
      notFound: (id) => {
        this.iterateListeners((cb) => cb.itemNotFound?.(id));
      }
    });
  }

  protected patch(event: { data: UserMFAFactorResource; object: AbstractMFAFactorModel }): any {
    event.object.options.model = event.data;
  }

  /**
   * Helper for requesting authentication needed to create
   * a sensitive profile user accounts hub client
   */
  requestMFAAuthenticationToken = () => {
    // Allow the user to authenticate
    this.showMFARequestLayer();

    return new Promise<string>((resolve, reject) => {
      this.authenticationHandlers.push({
        resolver: resolve,
        rejecter: reject
      });
    });
  };

  private showMFARequestLayer() {
    if (!this.MFAAuthenticationLayer) {
      this.MFAAuthenticationLayer = new Layer({
        userCanExit: false,
        withBackdrop: true,
        render: (event) => (
          <SensitiveUserAuthWidget
            onAuthenticated={(token) => {
              // resolve all promises in the queue for now
              this.authenticationHandlers.forEach((handler) => handler.resolver(token));
              this.authenticationHandlers = [];
              event.layer.dispose();
              this.MFAAuthenticationLayer = null;
            }}
            onCancel={() => {
              this.authenticationHandlers.forEach((handler) =>
                handler.rejecter(new Error('User did not enter credentials'))
              );
              this.authenticationHandlers = [];
              event.layer.dispose();
              this.MFAAuthenticationLayer = null;
            }}
          />
        )
      });

      this.system.layerManager.registerLayer(this.MFAAuthenticationLayer);
    }
  }
}
